import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheService } from '../cache';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PaymentService {

    /**
    * Creates an instance of the PaymentService
    *
    * @param {HttpClient} http Injected instance of HttpClient
    */
    constructor(private http: HttpClient,
        protected cache: CacheService
    ) { }


    /**
    * Sends a POST request to the /payments/initiate endpoint
    * @remarks
    * to initiate Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    initiatePayment(data: any) {
        return this.http.post(`payments/initiate`, data)
    }

    /**
    * Sends a POST request to the /payments/initiate/flw/booking endpoint
    * @remarks
    * to initiate Flutterwave Payment Link (Naira)
    *
    * @param {any} data body passed to the endpoint
    * @returns {Observable} Returns an observable
    */
    initiateFlutterwaveBookingPayment(data: any) {
        return this.http.post(`payments/initiate/flw/booking`, data)
    }

    /**
    * Sends a POST request to the /corporate/subscriptions/payments/initiate endpoint
    * @remarks
    * to initiate subscription Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    initiateSubscriptionPayment(data: any) {
        return this.http.post(`corporate/subscriptions/payments/initiate`, data)
    }

    /**
    * Sends a GET request to the payments/verify/stripe/ endpoint
    * @remarks
    * to verify payment made.
    *
    * @param {string} paymentIntentId The unique identifier used to verify the payment
    * @returns {Observable} Returns an observable
    */
    verifyPayment(paymentIntentId: string) {
        return this.http.get(`payments/verify/stripe/${paymentIntentId}`)
    }

    /**
    * Sends a GET request to the payments/verify/stripe/ endpoint
    * @remarks
    * to verify payment made.
    *
    * @param {string} paymentIntentId The unique identifier used to verify the payment
    * @returns {Observable} Returns an observable
    */
    verifySubscriptionPayment(paymentIntentId: string) {
        return this.http.get(`/corporate/subscriptions/payments/verify/stripe/${paymentIntentId}`)
    }

    /**
    * Sends a POST request to the payments/charge endpoint
    * @remark
    * to charge a card.
    *
    * @param {any} data body passed to the endpoint to charge a card
    * @returns {Observable} Returns an observable
    */
    charge_card(data: any) {
        return this.http.post('payments/charge', data)
    }

    /**
    * Sends a POST request to the /corporate/subscriptions/payments/charge endpoint
    * @remark
    * to charge a card.
    *
    * @param {any} data body passed to the endpoint to charge a card
    * @returns {Observable} Returns an observable
    */
    charge_Subscription_card(data: any) {
        return this.http.post('corporate/subscriptions/payments/charge', data)
    }

    /**
    * Sends a POST request to the payments/pay-later endpoint
    * @remark
    * to allow paying later. Thereby bypassing stripe payment system.
    *
    * @param {any} data body passed to the endpoint to charge a card
    * @returns {Observable} Returns an observable
    */
    pay_later(data: any) {
        return this.http.post('payments/pay-later', data)
    }

    /**
    * Sends a POST request to the payments/initiate/mondu endpoint
    * @remark
    * To initate module process by checking if the user is eligible
    *
    * @param {any} data body passed to the endpoint to charge a card
    * @returns {Observable} Returns an observable
    */
    initateModule(data: any) {
        return this.http.post('payments/initiate/mondu', data)
    }

    /**
    * Sends a GET request to the payments/verify/mondu/:id endpoint
    * @remark
    * To verify mondule payment with its UUID
    *
    * @param {any} data body passed to the endpoint to charge a card
    * @returns {Observable} Returns an observable
    */
    verifyModule(id: any) {
        return this.http.get(`payments/verify/mondu/${id}`)
    }

    /**
    * Sends a GET request to the payments/currencies endpoint
    * @remark
    * Get list of currencies.
    *
    * @returns {Observable} Returns an observable
    */
    getCurrencies() {
        return this.http.get('payments/currencies')
    }

    /**
    * Sends a GET request to the /featmapping endpoint
    * @remark
    * Get list of feature mappings.
    *
    * @returns {Observable} Returns an observable
    */
    getFeatureMappings() {
        return this.http.get('featmapping')
    }


    /**
    * Sends a GET request to the /corporate/subpackagegroup endpoint
    * @remark
    * To get a group subscription package.
    *
    * @returns {Observable} Returns an observable
    */
    getGroupSubscriptionPackage(params: any) {
        return this.http.get(`corporate/subpackagegroup`, { params })
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a GET request to the /corporate/subscriptions/:id endpoint
    * @remark
    * To get a subscribers detail.
    *
    * @returns {Observable} Returns an observable
    */
    getAllSubscribersList(params: any) {
        return this.http.get(`corporate/subscriptions`, { params })
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subscriptions/activate/:id endpoint
    * @remark
    * To activate a subscribers.
    *
    * @returns {Observable} Returns an observable
    */
    activateSubscriber(id: string) {
        return this.http.put(`corporate/subscriptions/activate/${id}`, {})
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subscriptions/deactivate/:id endpoint
    * @remark
    * To deactivate a subscribers.
    *
    * @returns {Observable} Returns an observable
    */
    deactivateSubscriber(id: string) {
        return this.http.put(`corporate/subscriptions/deactivate/${id}`, {})
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subscriptions/cancel/:id endpoint
    * @remark
    * To cancel a subscribers subscription.
    *
    * @returns {Observable} Returns an observable
    */
    cancelSubscriberSubscription(id: string) {
        return this.http.put(`corporate/subscriptions/cancel/${id}`, {})
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a GET request to the /corporate/subscriptions endpoint
    * @remark
    * To get a all subscribers list.
    *
    * @returns {Observable} Returns an observable
    */
    getSubscribersDetails(id: string) {
        return this.http.get(`corporate/subscriptions/${id}`)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a GET request to the /corporate/subpackagegroup/ endpoint
    * @remark
    * To get a group subscription package details.
    *
    * @returns {Observable} Returns an observable
    */
    getGroupSubscriptionPackageById(groupId: any) {
        return this.http.get(`corporate/subpackagegroup/${groupId}`)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }


    /**
    * Sends a POST request to the /corporate/subpackagegroup/ endpoint
    * @remark
    * To create a subscription package group.
    *
    * @returns {Observable} Returns an observable
    */
    createGroupSubscriptionPackage(payload: any) {
        return this.http.post('corporate/subpackagegroup', payload)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subpackagegroup/ endpoint
    * @remark
    * To create a subscription package group.
    *
    * @returns {Observable} Returns an observable
    */
    updateGroupSubscriptionPackage(groupId: string, payload: any) {
        return this.http.put(`corporate/subpackagegroup/${groupId}`, payload)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subpackagegroup/activate/:id endpoint
    * @remark
    * To activate a subscription package group.
    *
    * @returns {Observable} Returns an observable
    */
    activateGroupSubscriptionPackage(groupId: string, payload: any) {
        return this.http.put(`corporate/subpackagegroup/activate/${groupId}`, payload)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subpackagegroup/deactivate/:id endpoint
    * @remark
    * To deactivate a subscription package group.
    *
    * @returns {Observable} Returns an observable
    */
    deactivateGroupSubscriptionPackage(groupId: string, payload: any) {
        return this.http.put(`corporate/subpackagegroup/deactivate/${groupId}`, payload)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
* Sends a PUT request to the /corporate/subpackagegroup/ endpoint
* @remark
* To create a subscription package group.
*
* @returns {Observable} Returns an observable
*/
    deleteGroupSubscriptionPackage(groupId: string) {
        return this.http.delete(`corporate/subpackagegroup/${groupId}`)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }



    /**
    * Sends a GET request to the /corporate/subpackage endpoint
    * @remark
    * To get subscription packages.
    *
    * @returns {Observable} Returns an observable
    */
    getSubscriptionPackages(params?: any) {
        return this.http.get(`corporate/subpackage`, { params })
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }



    /**
    * Sends a POST request to the /corporate/subpackage/ endpoint
    * @remark
    * To add a subscription package to a group.
    *
    * @returns {Observable} Returns an observable
    */
    addSubscriptionPackage(payload: any) {
        return this.http.post('corporate/subpackage', payload)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }


    /**
    * Sends a PUT request to the /corporate/subpackage/ endpoint
    * @remark
    * To update a subscription package in a group.
    *
    * @returns {Observable} Returns an observable
    */
    updateSubscriptionPackage(packageId: string, payload: any) {
        return this.http.put(`corporate/subpackage/${packageId}`, payload)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a DELETE request to the /corporate/subpackage/ endpoint
    * @remark
    * To delete a subscription package in a group.
    *
    * @returns {Observable} Returns an observable
    */
    deleteSubscriptionPackage(packageId: string) {
        return this.http.delete(`corporate/subpackage/${packageId}`)
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subpackage/activate/:id endpoint
    * @remark
    * To activate a subscription package in a group.
    *
    * @returns {Observable} Returns an observable
    */
    activateSubscriptionPackage(packageId: string) {
        return this.http.put(`corporate/subpackage/activate/${packageId}`, {})
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }

    /**
    * Sends a PUT request to the /corporate/subpackage/deactivate/:id endpoint
    * @remark
    * To deactivate a subscription package in a group.
    *
    * @returns {Observable} Returns an observable
    */
    deactivateSubscriptionPackage(packageId: string) {
        return this.http.put(`corporate/subpackage/deactivate/${packageId}`, {})
            .pipe(map((resp: any) => {
                this.cache.remove(`corporate`);
                this.cache.remove(`corporate`);
                return resp;
            }));
    }



    /**
    * Sends a POST request to the generate revolute link /payments/initiate endpoint
    * @remarks
    * to initiate Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    generateRevolutePaymentLink(data: any) {
        return this.http.post(`payments/initiate/revolut`, data)
    }


    /**
    * Sends a POST request to the generate ratehawk order /ratehawk/order endpoint
    * @remarks
    * to initiate Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    generateRateHawkOrder(data: any) {
        return this.http.post(`ratehawk/order`, data)
    }

    /**
    * Sends a POST request to the generate ratehawk order /ratehawk/order/information endpoint
    * @remarks
    * to initiate Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    generateRateHawkOrderInfo(data: any) {
        return this.http.post(`ratehawk/order/information`, data)
    }


    /**
    * Sends a POST request to the finish ratehawk booking /ratehawk/order/finish endpoint
    * @remarks
    * to initiate Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    finishRateHawkOrder(data: any) {
        return this.http.post(`ratehawk/order/finish`, data)
    }

    /**
    * Sends a POST request to the confirm finish status ratehawk booking /ratehawk/order/finish/status endpoint
    * @remarks
    * to initiate Payment
    *
    * @param {Resource} item An instance of a resource to create
    * @returns {Observable} Returns an observable
    */
    finishRateHawkOrderStatus(data: any) {
        return this.http.post(`ratehawk/order/finish/status`, data)
    }

    /**
    * Sends a GET request to the verify Revolute Payment payments/verify/revolut/:id endpoint
    * @remarks
    * to verify Payment
    *
    * @param {Resource} id id of the booking object to verify
    * @returns {Observable} Returns an observable
    */

    verifyRevolutePayment(id: any) {
        return this.http.get(`payments/verify/revolut/${id}`)
    }

    /**
    * Sends a GET request to the verify Flutterwave Payment payments/verify/flw/:id endpoint
    * @remarks
    * to verify Payment
    *
    * @param {Resource} id transaction ID or reference to verify
    * @returns {Observable} Returns an observable
    */
    verifyFlutterwavePayment(id: any) {
        return this.http.get(`payments/verify/flw/${id}`)
    }

    /**
    * Sends a POST request to the process payments/process-booking endpoint
    * @remarks
    * to verify Payment
    *
    * @param {Resource} payload payload of the booking object to verify
    * @returns {Observable} Returns an observable
    */

    processPayment(payload: any) {
        return this.http.post(`payments/process-booking`, payload)
    }



}
